// https://github.com/sersavan/shadcn-multi-select-component
import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { CheckIcon, ChevronDown, X, WandSparkles } from 'lucide-react'
import { cn } from '../cn'
import { Badge } from './badge'
import { Button } from './button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './command'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Separator } from './separator'

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  // ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300
  'm-1 transition ',
  {
    variants: {
      variant: {
        default:
          'border-foreground/10 text-foreground bg-card hover:bg-card/80',
        secondary:
          'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        inverted: 'inverted',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    /** The text to display for the option. */
    label: string
    /** Secondary option text, also searchable; omitted from selected badges. */
    description?: string
    /** The unique value associated with the option. */
    value: string
    /** Optional icon component to display alongside the option. */
    icon?: React.ComponentType<{ className?: string }>
    /** Render before Select All and exclude from its selection. */
    excludeFromSelectAll?: boolean
  }[]

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void

  /**
   * Controlled selected values. When provided, the component mirrors this value.
   */
  value?: string[]

  /** The default selected values when the component mounts. */
  defaultValue?: string[]

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string

  /**
   * Additional class names for selected value badges shown inside the trigger.
   */
  tagClassName?: string

  /** Additional class names for the popover content. */
  popoverClassName?: string

  /** Minimum viewport spacing used when positioning the popover. */
  popoverCollisionPadding?: number

  /** Keep staged selections in the popover without rendering trigger badges. */
  showSelectedValues?: boolean

  /**
   * Hide the clear-all affordance rendered at the trigger's right side.
   */
  hideClearButton?: boolean

  /**
   * Hide the clear-all and close affordances rendered at the bottom of the popover.
   */
  hideClearAndClose?: boolean

  /** Label for the optional footer confirmation action. */
  confirmLabel?: string

  /**
   * Called from the footer confirmation action with the staged selections.
   * When omitted, the footer keeps its default close-only behavior.
   */
  onConfirm?: (value: string[]) => void
}

export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      options,
      onValueChange,
      value,
      variant,
      defaultValue = [],
      placeholder = 'Select options',
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      asChild = false,
      className,
      tagClassName,
      popoverClassName,
      popoverCollisionPadding,
      showSelectedValues = true,
      hideClearButton = false,
      hideClearAndClose = false,
      confirmLabel = 'Confirm',
      onConfirm,
      ...props
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] = React.useState<string[]>(
      value ?? defaultValue
    )
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)
    const [isAnimating, setIsAnimating] = React.useState(false)

    React.useEffect(() => {
      if (value === undefined) return
      setSelectedValues(value)
    }, [value])

    const updateSelectedValues = (nextSelectedValues: string[]) => {
      if (value === undefined) {
        setSelectedValues(nextSelectedValues)
      }
      onValueChange(nextSelectedValues)
    }

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true)
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues]
        newSelectedValues.pop()
        updateSelectedValues(newSelectedValues)
      }
    }

    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option]
      updateSelectedValues(newSelectedValues)
    }

    const handleClear = () => {
      updateSelectedValues([])
    }

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev)
    }

    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount)
      updateSelectedValues(newSelectedValues)
    }

    const selectAllOptions = options.filter(
      (option) => !option.excludeFromSelectAll
    )
    const allOptionsSelected = selectAllOptions.every((option) =>
      selectedValues.includes(option.value)
    )

    const toggleAll = () => {
      if (allOptionsSelected) {
        handleClear()
      } else {
        const allValues = selectAllOptions.map((option) => option.value)
        updateSelectedValues(allValues)
      }
    }

    const stopTriggerToggle = (
      event: React.MouseEvent<SVGSVGElement> | React.PointerEvent<SVGSVGElement>
    ) => {
      event.preventDefault()
      event.stopPropagation()
    }

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            asChild={asChild}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              'flex h-auto w-full items-center justify-between rounded-sm border bg-inherit hover:bg-inherit',
              className,
              showSelectedValues && selectedValues.length > 0
                ? 'px-2 py-1'
                : 'px-3 py-2'
            )}
          >
            {showSelectedValues && selectedValues.length > 0 ? (
              <div className='flex w-full items-center justify-between'>
                <div className='flex flex-wrap items-center gap-1'>
                  {selectedValues.slice(0, maxCount).map((value) => {
                    const option = options.find((o) => o.value === value)
                    const IconComponent = option?.icon
                    return (
                      <Badge
                        key={value}
                        className={cn(
                          '!m-0 [&>svg]:!pointer-events-auto',
                          isAnimating ? 'animate-bounce' : '',
                          multiSelectVariants({ variant }),
                          tagClassName
                        )}
                        style={{
                          animationDuration: `${animation}s`,
                          whiteSpace: 'break-spaces',
                        }}
                      >
                        {IconComponent && <IconComponent className='h-4 w-4' />}
                        {option?.label}
                        <X
                          className='h-3 w-3 cursor-pointer'
                          onPointerDown={stopTriggerToggle}
                          onMouseDown={stopTriggerToggle}
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            toggleOption(value)
                          }}
                        />
                      </Badge>
                    )
                  })}
                  {selectedValues.length > maxCount && (
                    <Badge
                      className={cn(
                        'text-foreground border-foreground/1 bg-transparent hover:bg-transparent [&>svg]:!pointer-events-auto',
                        isAnimating ? 'animate-bounce' : '',
                        multiSelectVariants({ variant }),
                        tagClassName
                        
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      {`+ ${selectedValues.length - maxCount} more`}
                      <X
                        className='h-4 w-4 cursor-pointer'
                        onPointerDown={stopTriggerToggle}
                        onMouseDown={stopTriggerToggle}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          clearExtraOptions()
                        }}
                      />
                    </Badge>
                  )}
                </div>
                <div className='flex items-center justify-between'>
                  {!hideClearButton && (
                    <X
                      className='text-muted-foreground mx-2 h-4 cursor-pointer !pointer-events-auto'
                      onPointerDown={stopTriggerToggle}
                      onMouseDown={stopTriggerToggle}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        handleClear()
                      }}
                    />
                  )}
                  {!selectedValues?.length > 0 && (
                    <>
                      <Separator
                        orientation='vertical'
                        className='flex h-full min-h-6'
                      />
                      <ChevronDown className='text-muted-foreground mx-2 h-4 cursor-pointer' />
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className='mx-auto flex w-full items-center justify-between'>
                <span className='text-muted-foreground text-sm leading-tight'>
                  {placeholder}
                </span>
                <ChevronDown className='text-muted-foreground h-4 cursor-pointer' />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            'w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-0',
            popoverClassName
          )}
          align='start'
          collisionPadding={popoverCollisionPadding}
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            <CommandInput
              placeholder='Search...'
              onKeyDown={handleInputKeyDown}
            />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options
                  .filter((option) => option.excludeFromSelectAll)
                  .map((option) => {
                    const isSelected = selectedValues.includes(option.value)
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        keywords={[option.label, option.description ?? '']}
                        onSelect={() => toggleOption(option.value)}
                        className='cursor-pointer'
                      >
                        <div
                          className={cn(
                            'border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                            isSelected
                              ? 'bg-primary text-primary-foreground'
                              : 'opacity-50 [&_svg]:invisible'
                          )}
                        >
                          <CheckIcon className='text-background h-4 w-4' />
                        </div>
                        {option.icon && (
                          <option.icon className='text-muted-foreground mr-2 h-4 w-4' />
                        )}
                        <span className={option.description ? 'flex min-w-0 flex-col' : undefined}>
                          <span className={option.description ? 'truncate' : undefined}>{option.label}</span>
                          {option.description && <span className='truncate text-xs text-muted-foreground'>{option.description}</span>}
                        </span>
                      </CommandItem>
                    )
                  })}
                <CommandItem
                  key='all'
                  onSelect={toggleAll}
                  className='cursor-pointer'
                >
                  <div
                    className={cn(
                      'border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                      allOptionsSelected && selectAllOptions.length > 0
                        ? 'bg-primary text-primary-foreground'
                        : 'opacity-50 [&_svg]:invisible'
                    )}
                  >
                    <CheckIcon className='text-background h-4 w-4' />
                  </div>
                  <span>(Select All)</span>
                </CommandItem>
                {selectAllOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value)
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      keywords={[option.label, option.description ?? '']}
                      onSelect={() => toggleOption(option.value)}
                      className='cursor-pointer'
                    >
                      <div
                        className={cn(
                          'border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'opacity-50 [&_svg]:invisible'
                        )}
                      >
                        <CheckIcon className='text-background h-4 w-4' />
                      </div>
                      {option.icon && (
                        <option.icon className='text-muted-foreground mr-2 h-4 w-4' />
                      )}
                      <span className={option.description ? 'flex min-w-0 flex-col' : undefined}>
                        <span className={option.description ? 'truncate' : undefined}>{option.label}</span>
                        {option.description && <span className='truncate text-xs text-muted-foreground'>{option.description}</span>}
                      </span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
            {!hideClearAndClose && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <div className='flex items-center justify-between'>
                    {!onConfirm && selectedValues.length > 0 && (
                      <>
                        <CommandItem
                          onSelect={handleClear}
                          className='flex-1 cursor-pointer justify-center'
                        >
                          Clear
                        </CommandItem>
                        <Separator
                          orientation='vertical'
                          className='flex h-full min-h-6'
                        />
                      </>
                    )}
                    <CommandItem
                      onSelect={() => {
                        if (onConfirm) onConfirm(selectedValues)
                        setIsPopoverOpen(false)
                      }}
                      className='max-w-full flex-1 cursor-pointer justify-center'
                    >
                      {onConfirm ? confirmLabel : 'Close'}
                    </CommandItem>
                  </div>
                </CommandGroup>
              </>
            )}
          </Command>
        </PopoverContent>
        {animation > 0 && selectedValues.length > 0 && (
          <WandSparkles
            className={cn(
              'text-foreground bg-background my-2 h-3 w-3 cursor-pointer',
              isAnimating ? '' : 'text-muted-foreground'
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )}
      </Popover>
    )
  }
)

MultiSelect.displayName = 'MultiSelect'
